/**
 * The arithmetic of the credit-confirm modal (PREC-4263), kept pure so every state the dialog can
 * show is pinned by a spec: what each part of a selection costs, the total, whether the total is a
 * maximum ("up to") because the action charges only for results, and what the balance says about
 * it. The modal and the cost block embedded in the custom-column forms render this and nothing
 * else, so the number on the button and the number in the breakdown cannot disagree.
 */

/** One part of a selection, as the modal shows it. */
export interface CreditSegmentView {
	/** Stable key of the row. */
	id: string;
	/** The row's text, e.g. "89 leads · searched, 1 credit per email found". */
	label: string;
	/** Leads or companies in this part. */
	count: number;
	/** What one of them costs when it is charged; 0 when skipped or free. */
	unitCredits: number;
	/**
	 * The charges the part can hold when one lead holds more than one (the email lookup searches a
	 * lead's person, job offer and Maps business, PREC-4263 review M1); `count` when absent.
	 */
	chargedUnits?: number;
	/** Whether the action runs on this part (a skipped part is shown, but not run). */
	included: boolean;
	/** Whether the charge depends on a result (an email found), so the cost is a maximum. */
	resultDependent?: boolean;
	/**
	 * Whether what the part costs is known only once the action runs: how many leads a run covers
	 * that the form cannot count, or how many people a preview finds (PREC-4263 review S1, M2). Its
	 * label names the price per unit; it adds nothing to the total, which then says it is partly
	 * decided by the run instead of promising a number.
	 */
	variable?: boolean;
}

/** The balance as the modal knows it: still loading, failed to load, or loaded. */
export type CreditBalanceState =
	| { status: "loading" }
	| { status: "unavailable" }
	| { status: "loaded"; credits: number };

export interface CreditBreakdownRow extends CreditSegmentView {
	credits: number;
}

export interface CreditBreakdown {
	/** The parts with at least one lead or company in them. */
	rows: CreditBreakdownRow[];
	/** Rows are listed only when the selection splits into more than one part. */
	showRows: boolean;
	/** Credits the action costs, or at most costs when `isUpTo`; the known part when `isVariable`. */
	total: number;
	/** Some of the cost is decided by the run, so `total` is only what is known before it. */
	isVariable: boolean;
	/** Credits the balance must hold for the action to start, when the API checks more than `total`. */
	requiredToStart: number;
	/** The total is a maximum: some of it is charged only for results. */
	isUpTo: boolean;
	/** Leads or companies the action runs on. */
	workCount: number;
	/**
	 * The balance after the action, at least this much when `isUpTo`, at most this much when
	 * `isVariable`; null while unknown.
	 */
	balanceAfter: number | null;
	/** A loaded balance that does not cover the total (or what the action needs to start). */
	isInsufficient: boolean;
	/** Credits missing to cover the total; 0 when covered or unknown. */
	shortfall: number;
	/** Whether the confirm button may be clicked. */
	canConfirm: boolean;
}

/** Credits are counted in hundredths: 104 × 0.2 is 20.8, never 20.800000000000004. */
export const roundCredits = (value: number): number => Math.round(value * 100) / 100;

/** A credit amount as the UI writes it: "1,420", "20.8". */
export const formatCredits = (value: number): string =>
	roundCredits(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

/** Leads or companies an action runs on: the parts it includes, not the ones it skips. */
export const countIncluded = (segments: CreditSegmentView[]): number =>
	segments.reduce((sum, segment) => sum + (segment.included ? segment.count : 0), 0);

const creditsOf = (segment: CreditSegmentView): number =>
	segment.included && !segment.variable
		? roundCredits((segment.chargedUnits ?? segment.count) * segment.unitCredits)
		: 0;

export interface CreditBreakdownOptions {
	/**
	 * What the API needs the balance to hold before it starts, when that is more than the known
	 * total (a preview's admission check, PREC-4263 review M2). Not a price: it is never the total.
	 */
	requiredToStart?: number;
}

/**
 * What a selection costs and what the balance says about it. The confirm button waits while the
 * balance loads, is disabled when a loaded balance does not cover the total or what the action
 * needs to start (or nothing would run), and stays enabled when the balance could not be loaded:
 * the API's pre-flight decides then. A variable part runs, so it counts as work even with no count.
 */
export function buildCreditBreakdown(
	segments: CreditSegmentView[],
	balance: CreditBalanceState,
	options: CreditBreakdownOptions = {}
): CreditBreakdown {
	const rows = segments
		.filter((segment) => segment.count > 0 || (segment.variable && segment.included))
		.map((segment) => ({ ...segment, credits: creditsOf(segment) }));
	const total = roundCredits(rows.reduce((sum, row) => sum + row.credits, 0));
	const isVariable = rows.some((row) => row.variable);
	const isUpTo = !isVariable && rows.some((row) => row.credits > 0 && row.resultDependent);
	const workCount = countIncluded(rows);
	const requiredToStart = Math.max(total, roundCredits(options.requiredToStart ?? 0));
	const balanceAfter = balance.status === "loaded" ? roundCredits(balance.credits - total) : null;
	const shortfall =
		balance.status === "loaded" ? Math.max(0, roundCredits(requiredToStart - balance.credits)) : 0;

	return {
		rows,
		showRows: rows.length > 1 || isVariable,
		total,
		isVariable,
		requiredToStart,
		isUpTo,
		workCount,
		balanceAfter,
		isInsufficient: shortfall > 0,
		shortfall,
		canConfirm: balance.status !== "loading" && shortfall === 0 && (workCount > 0 || isVariable),
	};
}
